import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { guestApi } from "@/lib/guest";
import { money, asset } from "@/lib/format";
import { publicOrigin, ensureHttps } from "@/lib/https";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { ShoppingBag, Plus, Minus, Leaf, Beef, ChefHat, Search, Loader2, MapPin } from "lucide-react";

export default function Storefront() {
  const { slug } = useParams();
  const [cart, setCart] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [search, setSearch] = useState("");
  const [cust, setCust] = useState({ name: "", phone: "" });
  const [checkingOut, setCheckingOut] = useState(false);
  const [payMethod, setPayMethod] = useState("stripe");

  const { data, isLoading } = useQuery({
    queryKey: ["store-bootstrap", slug],
    queryFn: async () => (await api.get(guestApi(slug, "/bootstrap"))).data,
    enabled: !!slug,
  });
  const { data: rzpCfg } = useQuery({ queryKey: ["rzp-config"], queryFn: async () => (await api.get("/payments/razorpay/config")).data });
  const settings = data?.settings;
  const cats = data?.categories || [];
  const sym = settings?.currency_symbol || "₹";

  const filtered = useMemo(() => (data?.items || []).filter((m) =>
    (activeCat === "all" || m.category_id === activeCat) &&
    (!search || m.name.toLowerCase().includes(search.toLowerCase()))), [data?.items, activeCat, search]);
  const items = data?.items || [];

  const add = (m) => setCart((c) => {
    const ex = c.find((x) => x.menu_item_id === m.id);
    if (ex) return c.map((x) => x.menu_item_id === m.id ? { ...x, qty: x.qty + 1 } : x);
    return [...c, { menu_item_id: m.id, name: m.name, price: m.price, qty: 1, tax_rate: m.tax_rate }];
  });
  const chg = (id, d) => setCart((c) => c.map((x) => x.menu_item_id === id ? { ...x, qty: Math.max(0, x.qty + d) } : x).filter((x) => x.qty > 0));

  const subtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const tax = cart.reduce((s, i) => s + i.price * i.qty * (i.tax_rate / 100), 0);
  const total = subtotal + tax;
  const count = cart.reduce((s, i) => s + i.qty, 0);

  const loadRazorpayScript = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  if (!slug) {
    return (
      <div className="min-h-screen grid place-items-center p-8 text-center">
        <div>
          <h1 className="font-display text-2xl font-800">Open this restaurant’s storefront</h1>
          <p className="text-neutral-500 mt-2">Guest ordering lives at <code>/r/your-slug/order</code>. Find the link in Settings after you sign up.</p>
        </div>
      </div>
    );
  }

  const checkout = async () => {
    if (!cart.length) return toast.error("Your cart is empty");
    if (!cust.name || !cust.phone) return toast.error("Please enter your name and phone");
    setCheckingOut(true);
    try {
      const { data: order } = await api.post(guestApi(slug, "/orders"), {
        items: cart, customer_name: cust.name, customer_phone: cust.phone,
      });
      if (payMethod === "razorpay") {
        const ok = await loadRazorpayScript();
        if (!ok) { toast.error("Could not load Razorpay"); setCheckingOut(false); return; }
        const { data: ro } = await api.post("/payments/razorpay/order", { order_id: order.id });
        const rzp = new window.Razorpay({
          key: ro.key_id, amount: ro.amount, currency: ro.currency,
          name: settings?.restaurant_name || "ojent", order_id: ro.razorpay_order_id,
          prefill: { name: cust.name, contact: cust.phone },
          theme: { color: "#E85D04" },
          handler: async (res) => {
            try {
              await api.post("/payments/razorpay/verify", {
                order_id: order.id, razorpay_order_id: res.razorpay_order_id,
                razorpay_payment_id: res.razorpay_payment_id, razorpay_signature: res.razorpay_signature,
              });
              window.location.href = `/payment/success?provider=razorpay&slug=${encodeURIComponent(slug)}`;
            } catch { toast.error("Payment verification failed"); }
          },
          modal: { ondismiss: () => setCheckingOut(false) },
        });
        rzp.open();
        return;
      }
      const { data: session } = await api.post("/payments/checkout", {
        order_id: order.id, origin_url: publicOrigin(),
      });
      window.location.href = ensureHttps(session.checkout_url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed. Please try again.");
      setCheckingOut(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--kul-app-bg)] font-customer">
      {/* Hero */}
      <div className="relative h-64 sm:h-80 bg-[var(--kul-charcoal)] overflow-hidden">
        <img src="https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1400&q=80" alt="restaurant"
          className="absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--kul-charcoal)] via-[var(--kul-charcoal)]/30 to-transparent" />
        <div className="relative z-10 max-w-5xl mx-auto px-6 h-full flex flex-col justify-end pb-8">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 rounded-lg bg-[var(--kul-primary)] flex items-center justify-center"><ChefHat className="w-5 h-5 text-white" /></div>
            <span className="text-white/80 text-sm tracking-widest uppercase">Order Online</span>
          </div>
          <h1 className="font-customer text-4xl sm:text-5xl font-800 text-white tracking-tight">{settings?.restaurant_name || "ojent"}</h1>
          <p className="text-white/70 mt-2 flex items-center gap-1.5 text-sm"><MapPin className="w-4 h-4" /> {settings?.address || "Fresh food, delivered fast"}</p>
          {settings?.hours && <p className="text-white/60 text-sm mt-1">{settings.hours}</p>}
          {settings?.about && <p className="text-white/70 text-sm mt-2 max-w-xl">{settings.about}</p>}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-6">
        {/* Search + categories */}
        <div className="sticky top-0 z-20 bg-[var(--kul-app-bg)] py-3 -mx-6 px-6">
          <div className="relative max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search dishes…" className="pl-9 h-11 bg-white" data-testid="store-search" />
          </div>
          <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
            <button onClick={() => setActiveCat("all")} className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap border ${activeCat === "all" ? "bg-[var(--kul-primary)] text-white border-[var(--kul-primary)]" : "bg-white border-neutral-200"}`}>All</button>
            {cats.map((c) => <button key={c.id} onClick={() => setActiveCat(c.id)} className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap border ${activeCat === c.id ? "bg-[var(--kul-primary)] text-white border-[var(--kul-primary)]" : "bg-white border-neutral-200"}`}>{c.name}</button>)}
          </div>
        </div>

        {isLoading ? <p className="text-neutral-400 py-10">Loading menu…</p> : (
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            {filtered.map((m) => {
              const inCart = cart.find((x) => x.menu_item_id === m.id);
              return (
                <div key={m.id} className="kul-card p-4 flex gap-4 animate-fade-up" data-testid={`store-item-${m.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      {m.is_veg ? <span className="w-4 h-4 rounded border border-green-600 flex items-center justify-center"><Leaf className="w-2.5 h-2.5 text-green-600" /></span>
                        : <span className="w-4 h-4 rounded border border-red-600 flex items-center justify-center"><Beef className="w-2.5 h-2.5 text-red-600" /></span>}
                      <h3 className="font-semibold font-customer truncate">{m.name}</h3>
                    </div>
                    <div className="font-mono-k font-semibold text-[var(--kul-charcoal)] mt-1">{money(m.price, sym)}</div>
                    <p className="text-sm text-neutral-500 mt-1 line-clamp-2">{m.description}</p>
                  </div>
                  <div className="w-28 shrink-0 flex flex-col items-center">
                    <div className="w-28 h-24 rounded-lg overflow-hidden bg-neutral-100">
                      {m.image && <img src={asset(m.image)} alt={m.name} className="w-full h-full object-cover" />}
                    </div>
                    {inCart ? (
                      <div className="flex items-center gap-2 mt-2 bg-[var(--kul-primary)] rounded-lg text-white px-2 py-1">
                        <button onClick={() => chg(m.id, -1)} data-testid={`store-minus-${m.id}`}><Minus className="w-4 h-4" /></button>
                        <span className="text-sm font-semibold w-5 text-center">{inCart.qty}</span>
                        <button onClick={() => add(m)} data-testid={`store-plus-${m.id}`}><Plus className="w-4 h-4" /></button>
                      </div>
                    ) : (
                      <Button size="sm" onClick={() => add(m)} data-testid={`store-add-${m.id}`}
                        className="mt-2 w-full bg-white border border-[var(--kul-primary)] text-[var(--kul-primary)] hover:bg-[var(--kul-primary)] hover:text-white font-semibold">ADD</Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Cart sheet */}
      <Sheet>
        <SheetTrigger asChild>
          <button data-testid="open-cart" className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-30 flex items-center gap-3 bg-[var(--kul-primary)] text-white px-6 py-3.5 rounded-full shadow-lg transition-transform hover:scale-105 ${count ? "" : "hidden"}`}>
            <ShoppingBag className="w-5 h-5" />
            <span className="font-semibold">{count} item{count > 1 ? "s" : ""}</span>
            <span className="font-mono-k font-bold">{money(total, sym)}</span>
          </button>
        </SheetTrigger>
        <SheetContent className="w-full sm:max-w-md flex flex-col">
          <SheetHeader><SheetTitle className="font-customer text-2xl">Your Order</SheetTitle></SheetHeader>
          <div className="flex-1 overflow-y-auto py-4 space-y-2">
            {cart.map((it) => (
              <div key={it.menu_item_id} className="flex items-center gap-3 p-2 rounded-lg bg-neutral-50">
                <div className="flex-1"><div className="text-sm font-medium">{it.name}</div><div className="text-xs text-neutral-400 font-mono-k">{money(it.price, sym)}</div></div>
                <div className="flex items-center gap-2 bg-white border rounded-lg px-2 py-1">
                  <button onClick={() => chg(it.menu_item_id, -1)}><Minus className="w-3.5 h-3.5" /></button>
                  <span className="text-sm w-5 text-center">{it.qty}</span>
                  <button onClick={() => chg(it.menu_item_id, 1)}><Plus className="w-3.5 h-3.5" /></button>
                </div>
                <span className="w-16 text-right text-sm font-mono-k font-semibold">{money(it.price * it.qty, sym)}</span>
              </div>
            ))}
          </div>
          <div className="border-t pt-4 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Your name" value={cust.name} onChange={(e) => setCust({ ...cust, name: e.target.value })} data-testid="store-name" />
              <Input placeholder="Phone" value={cust.phone} onChange={(e) => setCust({ ...cust, phone: e.target.value })} data-testid="store-phone" />
            </div>
            <div className="text-sm space-y-1">
              <div className="flex justify-between text-neutral-500"><span>Subtotal</span><span className="font-mono-k">{money(subtotal, sym)}</span></div>
              <div className="flex justify-between text-neutral-500"><span>Taxes</span><span className="font-mono-k">{money(tax, sym)}</span></div>
              <div className="flex justify-between font-bold text-lg pt-1"><span>Total</span><span className="font-mono-k text-[var(--kul-primary)]">{money(total, sym)}</span></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setPayMethod("stripe")} data-testid="pay-stripe"
                className={`py-2 rounded-lg border text-sm font-medium transition-colors ${payMethod === "stripe" ? "border-[var(--kul-primary)] bg-[var(--kul-primary)]/10 text-[var(--kul-primary)]" : "border-neutral-200 text-neutral-500"}`}>Card (Stripe)</button>
              <button onClick={() => setPayMethod("razorpay")} data-testid="pay-razorpay"
                className={`py-2 rounded-lg border text-sm font-medium transition-colors ${payMethod === "razorpay" ? "border-[var(--kul-primary)] bg-[var(--kul-primary)]/10 text-[var(--kul-primary)]" : "border-neutral-200 text-neutral-500"}`}>
                UPI / Razorpay {rzpCfg && !rzpCfg.enabled ? "•" : ""}
              </button>
            </div>
            <Button onClick={checkout} disabled={checkingOut} data-testid="store-checkout"
              className="w-full h-12 bg-[var(--kul-primary)] hover:bg-[var(--kul-primary-hover)] text-white font-bold text-base">
              {checkingOut ? <Loader2 className="w-5 h-5 animate-spin" /> : `Pay ${money(total, sym)}`}
            </Button>
            <p className="text-xs text-center text-neutral-400">Secure payment via Stripe · Test card 4242 4242 4242 4242</p>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
